import React, { useEffect, useRef, useState } from 'react';
import AOS from 'aos';
import 'aos/dist/aos.css';

import aiData       from '../assets/deeptech/ai-data.jpg';
import videoAnalytics from '../assets/deeptech/video-analytics.jpg';
import quantum      from '../assets/deeptech/quantum.jpg';
import iotCyber     from '../assets/deeptech/iot-cyber.jpg';
import industry40   from '../assets/deeptech/industry40.jpg';

const EXPERTISE = [
  {
    img: aiData,
    icon: 'fa-database',
    label: 'AI and Data Engineering',
    desc: 'Building intelligent pipelines, ML models, and data-driven systems at enterprise scale.',
    accent: '#6366f1',
  },
  {
    img: videoAnalytics,
    icon: 'fa-video-camera',
    label: 'Video Analytics Solutions',
    desc: 'Real-time computer vision for surveillance, retail analytics, and smart city applications.',
    accent: '#0ea5e9',
  },
  {
    img: quantum,
    icon: 'fa-flask',
    label: 'Quantum Computing',
    desc: 'Post-quantum cryptography, quantum-readiness assessments, and hybrid algorithm research.',
    accent: '#a855f7',
  },
  {
    img: iotCyber,
    icon: 'fa-shield',
    label: 'IoT and Cyber Security',
    desc: 'Securing connected ecosystems with zero-trust architectures and real-time threat detection.',
    accent: '#10b981',
  },
  {
    img: industry40,
    icon: 'fa-cogs',
    label: 'Industry 4.0 Migration',
    desc: 'Modernising manufacturing and operations with automation, digital twins, and smart factories.',
    accent: '#f59e0b',
  },
];


function ExpertiseCard({ img, icon, label, desc, accent }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        ...S.card,
        transform: hovered ? 'translateY(-6px)' : 'translateY(0)',
        boxShadow: hovered
          ? `0 20px 48px rgba(0,0,0,0.45), 0 0 0 1px ${accent}55`
          : '0 4px 20px rgba(0,0,0,0.35)',
      }}
    >
      {/* background image */}
      <img
        src={img}
        alt={label}
        style={{
          ...S.cardImg,
          transform: hovered ? 'scale(1.06)' : 'scale(1)',
        }}
      />

      {/* gradient overlay — deepens on hover */}
      <div style={{
        ...S.overlay,
        background: hovered
          ? `linear-gradient(to top, ${accent}ee 0%, rgba(0,0,0,0.65) 60%, rgba(0,0,0,0.2) 100%)`
          : 'linear-gradient(to top, rgba(0,0,0,0.88) 0%, rgba(0,0,0,0.4) 55%, rgba(0,0,0,0.1) 100%)',
      }} />

      {/* top accent stripe */}
      <div style={{ ...S.topStripe, background: accent }} />

      {/* icon badge */}
      <div style={{ ...S.iconBadge, background: accent, boxShadow: `0 4px 14px ${accent}88` }}>
        <i className={`fa ${icon}`} style={{ color: '#fff', fontSize: 15 }} />
      </div>

      {/* content at bottom */}
      <div style={S.cardContent}>
        <h3 style={S.cardTitle}>{label}</h3>
        <p style={{
          ...S.cardDesc,
          maxHeight: hovered ? 80 : 0,
          opacity: hovered ? 1 : 0,
        }}>
          {desc}
        </p>
      </div>
    </div>
  );
}

const CARD_W = 280; // 260px card + 20px gap
const SET_W  = EXPERTISE.length * CARD_W; // 5 × 280 = 1400

export const DeepTechSection = () => {
  const trackRef    = useRef(null);
  const resetting   = useRef(false);

  useEffect(() => {
    AOS.init({ duration: 700, once: true });
    // start in the middle copy so both directions have room to scroll
    if (trackRef.current) trackRef.current.scrollLeft = SET_W;
  }, []);

  const handleScroll = () => {
    const el = trackRef.current;
    if (!el || resetting.current) return;
    if (el.scrollLeft < SET_W) {
      resetting.current = true;
      el.style.scrollBehavior = 'auto';
      el.scrollLeft += SET_W;
      requestAnimationFrame(() => { resetting.current = false; });
    } else if (el.scrollLeft >= SET_W * 2) {
      resetting.current = true;
      el.style.scrollBehavior = 'auto';
      el.scrollLeft -= SET_W;
      requestAnimationFrame(() => { resetting.current = false; });
    }
  };

  const scroll = (dir) => {
    const el = trackRef.current;
    if (!el) return;
    el.style.scrollBehavior = 'smooth';
    el.scrollLeft += dir * CARD_W;
  };

  return (
    <div style={S.section}>
      <div style={S.blob1} />
      <div style={S.blob2} />

      <div className="container" style={{ position: 'relative', zIndex: 1 }}>

        {/* Header */}
        <div data-aos="fade-up" style={{ textAlign: 'center', marginBottom: 48 }}>
          <span style={S.eyebrow}>
            <span style={S.dot} />
            WHAT WE SPECIALIZE IN
          </span>
          <h2 style={S.heading}>Our DeepTech Expertise</h2>
          <p style={S.subheading}>
            Cutting-edge technology capabilities built for the next decade of innovation.
          </p>
        </div>

        {/* Infinite Carousel */}
        <div style={{ position: 'relative' }}>
          <button onClick={() => scroll(-1)} style={{ ...S.arrowBtn, left: -20 }}>
            <i className="fa fa-chevron-left" />
          </button>

          <div ref={trackRef} onScroll={handleScroll} style={S.track}>
            {[...EXPERTISE, ...EXPERTISE, ...EXPERTISE].map(({ img, icon, label, desc, accent }, i) => (
              <ExpertiseCard key={i} img={img} icon={icon} label={label} desc={desc} accent={accent} />
            ))}
          </div>

          <button onClick={() => scroll(1)} style={{ ...S.arrowBtn, right: -20 }}>
            <i className="fa fa-chevron-right" />
          </button>
        </div>

      </div>
    </div>
  );
};

const S = {
  section: {
    position: 'relative',
    background: 'linear-gradient(135deg, #0f172a 0%, #1e1b4b 55%, #0f172a 100%)',
    padding: '80px 0 72px',
    overflow: 'hidden',
  },
  blob1: {
    position: 'absolute', top: -140, left: '15%',
    width: 400, height: 400, borderRadius: '50%',
    background: 'rgba(99,102,241,0.07)', pointerEvents: 'none',
  },
  blob2: {
    position: 'absolute', bottom: -120, right: '10%',
    width: 320, height: 320, borderRadius: '50%',
    background: 'rgba(168,85,247,0.06)', pointerEvents: 'none',
  },
  eyebrow: {
    display: 'inline-flex', alignItems: 'center', gap: 8,
    background: 'rgba(255,255,255,0.07)',
    border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: 20, padding: '5px 16px',
    color: '#93c5fd', fontSize: 11, fontWeight: 700, letterSpacing: 1.2,
    marginBottom: 14,
  },
  dot: {
    width: 6, height: 6, borderRadius: '50%',
    background: '#4ade80', boxShadow: '0 0 6px #4ade80',
    display: 'inline-block', flexShrink: 0,
  },
  heading: {
    color: '#fff',
    fontSize: 'clamp(26px,3.5vw,38px)',
    fontWeight: 800, margin: '8px 0 12px', lineHeight: 1.2,
  },
  subheading: {
    color: '#94a3b8', fontSize: 15.5,
    maxWidth: 480, margin: '0 auto', lineHeight: 1.7,
  },

  /* carousel */
  track: {
    display: 'flex',
    gap: 20,
    overflowX: 'auto',
    scrollSnapType: 'x mandatory',
    paddingBottom: 8,
    scrollbarWidth: 'none',
    msOverflowStyle: 'none',
  },
  arrowBtn: {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    zIndex: 10,
    width: 44,
    height: 44,
    borderRadius: '50%',
    border: '1.5px solid rgba(255,255,255,0.25)',
    background: 'rgba(255,255,255,0.1)',
    backdropFilter: 'blur(8px)',
    color: '#fff',
    fontSize: 16,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  card: {
    position: 'relative',
    height: 300,
    minWidth: 260,
    flex: '0 0 260px',
    borderRadius: 16,
    overflow: 'hidden',
    cursor: 'pointer',
    scrollSnapAlign: 'start',
    transition: 'transform 0.35s ease, box-shadow 0.35s ease',
  },
  cardImg: {
    position: 'absolute', inset: 0,
    width: '100%', height: '100%',
    objectFit: 'cover',
    transition: 'transform 0.5s ease',
  },
  overlay: {
    position: 'absolute', inset: 0,
    transition: 'background 0.4s ease',
  },
  topStripe: {
    position: 'absolute', top: 0, left: 0, right: 0,
    height: 3,
  },
  iconBadge: {
    position: 'absolute', top: 16, right: 16,
    width: 34, height: 34, borderRadius: '50%',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  cardContent: {
    position: 'absolute', bottom: 0, left: 0, right: 0,
    padding: '18px 16px 18px',
  },
  cardTitle: {
    color: '#fff', fontSize: 14.5, fontWeight: 800,
    margin: '0 0 6px', lineHeight: 1.3, letterSpacing: 0.1,
  },
  cardDesc: {
    color: 'rgba(255,255,255,0.82)', fontSize: 12.5, lineHeight: 1.55,
    margin: 0, overflow: 'hidden',
    transition: 'max-height 0.35s ease, opacity 0.3s ease',
  },

};
